import {
  ChevronDownIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/24/outline"
import { useMutation } from "@tanstack/react-query"
import { Checkbox, Form, InputNumber, Select } from "antd"
import { P2 } from "components"
import { useState } from "react"
import { useAppNotificationStore } from "stores"
import { IWalletBalance } from "pages/dashboard/wallets/interface/wallet"
import { walletAxiosInstance } from "utils/request"
import type { IFormData } from "../contractor/ContractorPaymentSchedule"

type PersonsUpdater = IFormData[] | ((prev: IFormData[]) => IFormData[])
type SetPersons = (updater: PersonsUpdater) => void

/** How amounts are entered for one group of beneficiaries in a Multiple schedule. */
export type AmountMode = "individual" | "sameAmount" | "sameValue"

export interface AmountSetting {
  mode: AmountMode
  // /** single value shared across everyone when `mode !== "individual"
  sharedAmount: number | null
  /** wallet currency the shared value is expressed in, used when `mode === "sameValue"` */
  sharedCurrency: string
}

export const sampleAmountSetting: AmountSetting = {
  mode: "individual",
  sharedAmount: null,
  sharedCurrency: "USD",
}

const SHARED_LOCAL_CURRENCY = "__LOCAL__"

const formatAmount = (value: any) =>
  `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
const parseAmount = (value: any) =>
  `${value}`.replace(/,/g, "") as unknown as number

/**
 * FX-rate cache for one amount-settings section. The parent calls this once per
 * section (primary + "added at the bottom") and hands the pieces to
 * `BeneficiaryAmountSettings` and to its own `Select` handlers, so both read the
 * same rates without any cross-component syncing.
 */
export const useConversionRates = (setPersons: SetPersons) => {
  const { toast } = useAppNotificationStore()
  const [rates, setRates] = useState<Record<string, number>>({})

  const { mutate } = useMutation({
    mutationFn: async ({
      sourceCurrency,
      destinationCurrency,
    }: {
      sourceCurrency: string
      destinationCurrency: string
    }) => {
      const res = await walletAxiosInstance.post(
        `/v1/wallets/conversion-rate`,
        { sourceCurrency, destinationCurrency }
      )
      return { rate: res.data?.data?.rate as number, destinationCurrency }
    },
    onSuccess: ({ rate, destinationCurrency }) => {
      setRates((prev) => ({ ...prev, [destinationCurrency]: rate }))
      setPersons((prev: any) =>
        prev.map((per: any) =>
          per.currency_code === destinationCurrency
            ? {
                ...per,
                display_amount: per.amount
                  ? Number((Number(per.amount) * rate).toFixed(2))
                  : per.display_amount,
              }
            : per
        )
      )
    },
    onError: () => {
      toast.error("Conversion unavailable")
    },
  })

  const fetchRates = (
    sourceCurrency: string,
    destinationCurrencies: (string | undefined)[]
  ) => {
    ;[...new Set(destinationCurrencies)]
      .filter((c): c is string => !!c && c !== sourceCurrency)
      .forEach((destinationCurrency) =>
        mutate({ sourceCurrency, destinationCurrency })
      )
  }

  const resetRates = () => setRates({})

  return { rates, fetchRates, resetRates }
}

/** True while "use same value" is on and a needed conversion rate hasn't loaded. */
export const hasMissingRate = (
  persons: IFormData[],
  sharedCurrency: string,
  rates: Record<string, number>
) => {
  const needed = [
    ...new Set(
      persons
        .map((p) => p?.currency_code)
        .filter((c) => c && c !== sharedCurrency)
    ),
  ]
  return needed.some((c) => !rates[c as string])
}

const displayedAmount = (
  person: IFormData,
  setting: AmountSetting,
  _rates: Record<string, number>
) => {
  const amount =
    setting.mode === "sameValue" &&
    person?.currency_code !== setting.sharedCurrency
      ? person?.display_amount // converted equivalent, stored on the person
      : person?.amount
  return amount === "" || amount == null ? null : Number(amount)
}

interface Props {
  type: "employee" | "contractor"
  idKey: "person_id" | "contractor_id"
  persons: IFormData[]
  setPersons: SetPersons
  value: AmountSetting
  onChange: (next: AmountSetting) => void
  conversionRates: Record<string, number>
  fetchRates: (
    sourceCurrency: string,
    destinationCurrencies: (string | undefined)[]
  ) => void
  resetRates: () => void
  balances?: IWalletBalance[]
  getCurrencySymbol: (code: string | undefined) => string | undefined
}

const BeneficiaryAmountSettings = ({
  type,
  idKey,
  persons,
  setPersons,
  value,
  onChange,
  conversionRates,
  fetchRates,
  resetRates,
  balances,
  getCurrencySymbol,
}: Props) => {
  const [activeRowId, setActiveRowId] = useState("")

  if (!persons.length) return null

  const { mode, sharedAmount, sharedCurrency } = value
  const Label = type === "employee" ? "Employee" : "Contractor"
  const isShared = mode !== "individual"
  const hasEmptyAmount = persons.some(
    (p: any) => p.amount === "" || p.amount == null
  )

  const defaultWalletCurrency = balances?.some((b: any) => b.currency === "USD")
    ? "USD"
    : balances?.[0]?.currency || "USD"

  const rowId = (p: any): string =>
    (idKey === "person_id" ? p.person_id : p.contractor_id) ?? ""

  const getCurrencyOptions = () => {
    const first = persons[0]?.currency_code
    const allShare = persons.every((p) => p?.currency_code === first)
    const codes =
      allShare && first && first !== "USD" ? [String(first), "USD"] : ["USD"]
    return [
      { value: SHARED_LOCAL_CURRENCY, label: "Local" },
      ...codes.map((code) => ({ value: code, label: getCurrencySymbol(code) })),
    ]
  }

  const getSharedCurrencyValue = () => {
    const isLocalForAll = persons.every(
      (p) => String(p?.source_currency_code) === String(p?.currency_code)
    )
    if (isLocalForAll) return SHARED_LOCAL_CURRENCY
    const first = persons[0]?.source_currency_code
    const isSameForAll = persons.every(
      (p) => String(p?.source_currency_code) === String(first)
    )
    return isSameForAll ? first : undefined
  }

  const toggleMode = (next: AmountMode, checked: boolean) => {
    if (!checked) {
      onChange({ ...value, mode: "individual", sharedAmount: null })
      setPersons((prev: any) =>
        prev.map((per: any) => ({ ...per, amount: "", display_amount: null }))
      )
      return
    }

    if (next === "sameAmount") {
      onChange({ ...value, mode: "sameAmount", sharedAmount: null })
      setPersons((prev: any) =>
        prev.map((per: any) => ({
          ...per,
          source_currency_code: per?.currency_code || "USD",
          amount: "",
          display_amount: null,
        }))
      )
      return
    }

    onChange({
      ...value,
      mode: "sameValue",
      sharedAmount: null,
      sharedCurrency: defaultWalletCurrency,
    })
    setPersons((prev: any) =>
      prev.map((per: any) => ({
        ...per,
        source_currency_code: defaultWalletCurrency,
        amount: "",
        display_amount: null,
      }))
    )
    fetchRates(
      defaultWalletCurrency,
      persons.map((p) => p.currency_code)
    )
  }

  const changeSharedAmount = (raw: number | null) => {
    const amount = raw === null ? "" : raw
    onChange({ ...value, sharedAmount: raw })
    setPersons((prev: any) =>
      prev.map((per: any) => {
        if (mode !== "sameValue") return { ...per, amount }
        const sameCurrency = per.currency_code === sharedCurrency
        const rate = conversionRates[per.currency_code]
        return {
          ...per,
          amount,
          display_amount: sameCurrency
            ? amount
            : rate
            ? Number((Number(amount) * rate).toFixed(2))
            : null,
        }
      })
    )
  }

  const changeRowAmount = (person: any, raw: number | null) => {
    setPersons((prev: any) =>
      prev.map((per: any) =>
        rowId(per) === rowId(person) ? { ...per, amount: raw } : per
      )
    )
  }

  const changeRowCurrency = (person: any, currency: string) => {
    setPersons((prev: any) =>
      prev.map((per: any) =>
        rowId(per) === rowId(person)
          ? { ...per, source_currency_code: currency }
          : per
      )
    )
  }

  return (
    <div className="mt-4 flex flex-col">
      <span className="font-circular text-padeBlack text-sm mb-1">
        {Label} Amount Settings
        {hasEmptyAmount && (
          <span className="ml-1 text-xs mt-0.5 text-danger500">(required)</span>
        )}
      </span>

      <div className="flex items-center gap-2 mb-2">
        <Checkbox
          checked={mode === "sameAmount"}
          onChange={(e) => toggleMode("sameAmount", e.target.checked)}
        >
          Use same amount for all {Label.toLowerCase()}s
        </Checkbox>
        <Checkbox
          checked={mode === "sameValue"}
          onChange={(e) => toggleMode("sameValue", e.target.checked)}
        >
          Use same value for all {Label.toLowerCase()}s
        </Checkbox>
      </div>

      {mode === "sameValue" && (
        <div className="bg-primary50 flex justify-start items-center gap-3 py-4 px-4 mb-2">
          <ExclamationCircleIcon className="w-5 text-primary500" />
          <P2 className="text-neutral300">
            {Label}s in other currencies will receive equivalent converted
            amounts.
          </P2>
        </div>
      )}

      {isShared && (
        <InputNumber<number>
          formatter={formatAmount}
          parser={parseAmount}
          className="w-full font-circular text-neutral400 mb-2"
          addonBefore={
            mode === "sameAmount" ? (
              persons[0]?.source_currency_code && (
                <Select
                  popupMatchSelectWidth={false}
                  value={getSharedCurrencyValue()}
                  options={getCurrencyOptions()}
                  onChange={(currency) =>
                    setPersons((prev: any) =>
                      prev.map((per: any) => ({
                        ...per,
                        source_currency_code:
                          currency === SHARED_LOCAL_CURRENCY
                            ? per?.currency_code || "USD"
                            : currency,
                      }))
                    )
                  }
                />
              )
            ) : (
              <Select
                popupMatchSelectWidth={false}
                value={sharedCurrency}
                options={
                  balances?.map((b: any) => ({
                    value: b.currency,
                    label: b.symbol,
                  })) || []
                }
                onChange={(currency) => {
                  onChange({ ...value, sharedCurrency: currency })
                  setPersons((prev: any) =>
                    prev.map((per: any) => ({
                      ...per,
                      source_currency_code: currency,
                      display_amount: null,
                    }))
                  )
                  resetRates()
                  fetchRates(
                    currency,
                    persons.map((p) => p.currency_code)
                  )
                }}
              />
            )
          }
          value={sharedAmount}
          placeholder=""
          onChange={changeSharedAmount}
        />
      )}

      {persons.map((person, index) => (
        <div
          className="border border-[#C2C7D0] mb-2 rounded-5px overflow-hidden"
          key={index}
        >
          <div
            className={`p-3 cursor-pointer justify-between w-full flex items-center ${
              activeRowId === rowId(person) &&
              "border-b border-[#C2C7D0] bg-background"
            }`}
            onClick={() =>
              setActiveRowId(activeRowId === rowId(person) ? "" : rowId(person))
            }
          >
            <h6 className="text-neutral700 text-sm font-medium font-avenir">
              {person?.person_name}
            </h6>
            <ChevronDownIcon className="w-4 h-4" />
          </div>
          {activeRowId === rowId(person) && (
            <div className="px-3 mt-3">
              <Form layout="vertical">
                <Form.Item
                  label="Amount"
                  // No `name`: this field is controlled by the parent's persons
                  // state (value + onChange). Giving it a `name` makes AntD's
                  // form store own it too and blank it on remount. Validation is
                  // surfaced via `hasEmptyAmount` above instead.
                >
                  <InputNumber<number>
                    formatter={formatAmount}
                    parser={parseAmount}
                    className="w-full font-circular text-neutral400"
                    addonBefore={
                      mode === "sameValue"
                        ? person?.currency_symbol
                        : mode === "sameAmount"
                        ? getCurrencySymbol(person?.source_currency_code)
                        : person?.source_currency_code && (
                            <Select
                              popupMatchSelectWidth={false}
                              value={person?.source_currency_code}
                              options={[
                                {
                                  value: person?.currency_code,
                                  label: person?.currency_symbol,
                                },
                                ...(person?.currency_code !== "USD"
                                  ? [{ value: "USD", label: "$" }]
                                  : []),
                              ]}
                              onChange={(currency) =>
                                changeRowCurrency(person, currency)
                              }
                            />
                          )
                    }
                    value={displayedAmount(person, value, conversionRates)}
                    placeholder=""
                    readOnly={isShared}
                    onChange={(raw) => changeRowAmount(person, raw)}
                  />
                </Form.Item>
              </Form>
            </div>
          )}
        </div>
      ))}

      {hasEmptyAmount && (
        <span className="text-xs mt-0.5 text-danger500">
          All amounts fields are required!
        </span>
      )}
    </div>
  )
}

export default BeneficiaryAmountSettings
